import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, X, CheckCircle2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSummarize } from '@/hooks/use-ai'
import type { AiSummary } from '@/lib/types'

interface SummaryButtonProps {
  type: 'interaction' | 'voice_note'
  sourceId: string
}

export function SummaryButton({ type, sourceId }: SummaryButtonProps) {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const summarize = useSummarize()

  function handleSummarize() {
    setShowModal(true)
    summarize.mutate({ type, sourceIds: [sourceId] })
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          handleSummarize()
        }}
        disabled={summarize.isPending}
      >
        <Sparkles className="mr-1 size-3" />
        {t('ai.summarize')}
      </Button>

      {showModal && (
        <SummaryModalContent
          data={summarize.data}
          isLoading={summarize.isPending}
          error={summarize.error}
          onClose={() => {
            setShowModal(false)
            summarize.reset()
          }}
        />
      )}
    </>
  )
}

function SummaryModalContent({
  data,
  isLoading,
  error,
  onClose,
}: {
  data: AiSummary | undefined
  isLoading: boolean
  error: Error | null
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-md rounded-xl border p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-primary size-4" />
            <h2 className="text-base font-semibold">{t('ai.summaryTitle')}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </div>

        <div className="mt-4">
          {isLoading && (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
              <p className="text-muted-foreground text-sm">{t('ai.summarizing')}</p>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 text-destructive flex items-center gap-2 rounded-lg p-3 text-sm">
              <AlertCircle className="size-4 shrink-0" />
              {t('ai.summaryError')}
            </div>
          )}

          {data && (
            <div className="space-y-3">
              <p className="text-sm">{data.summary}</p>

              {data.keyPoints.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs font-medium">{t('ai.keyPoints')}</p>
                  <ul className="mt-1 space-y-1">
                    {data.keyPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-sm">
                        <span className="bg-primary mt-1 size-1 shrink-0 rounded-full" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.actionItems.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs font-medium">{t('ai.actionItems')}</p>
                  <ul className="mt-1 space-y-1">
                    {data.actionItems.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-sm">
                        <CheckCircle2 className="text-primary mt-0.5 size-3.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
